import React, { useState, useMemo, useCallback, useRef } from 'react';
import ApiService from '../../../_services/ApiService';

interface ActivitySlot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  instructorId: number;
  instructorName: string;
  topicMasterId: number;
  topicName: string;
  agenda: string;
  batchIds?: number[];
  attendanceTaken?: boolean;
  roomId?: number;
  roomName?: string;
  centreId?: number;
  centreName?: string;
}

interface BatchMapping {
  batchId: number;
  batchName: string;
  batchCode?: string;
}

interface TemplateDayViewProps {
  slots: ActivitySlot[];
  date: string;
  templateStartDate: string;
  templateEndDate: string;
  templateId?: number;
  isEditMode: boolean;
  onDateChange: (date: string) => void;
  onAddSlot: (date?: string) => void;
  onEditSlot: (slot: ActivitySlot) => void;
  onDeleteSlot: (slotId: number) => void;
  onSlotMoved: () => void;
  templateBatchIds?: number[];
  batchMappings?: BatchMapping[];
  onAttendance?: (batchIds: number[], templateSlotId: number, instructorId?: number, instructorName?: string) => void;
  onAttendanceNode?: (batchIds: number[], templateSlotId: number, instructorId?: number, instructorName?: string) => void;
}

const PIXELS_PER_30MIN = 40;
const SNAP_MINUTES = 15;

function parseLocalDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function timeToMinutes(time: string): number {
  const parts = time.split(':');
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTimeDisplay(time: string): string {
  if (!time) return '-';
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
}

function addDays(dateStr: string, days: number): string {
  const d = parseLocalDate(dateStr);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

const TemplateDayView: React.FC<TemplateDayViewProps> = ({
  slots,
  date,
  templateStartDate,
  templateEndDate,
  templateId,
  isEditMode,
  onDateChange,
  onAddSlot,
  onEditSlot,
  onDeleteSlot,
  onSlotMoved,
  templateBatchIds = [],
  batchMappings = [],
  onAttendance,
  onAttendanceNode,
}) => {
  const [draggedSlotId, setDraggedSlotId] = useState<number | null>(null);
  const [dragGhost, setDragGhost] = useState<{ top: number; height: number } | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const batchNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const b of batchMappings) {
      map.set(b.batchId, b.batchCode || b.batchName || `Batch ${b.batchId}`);
    }
    return map;
  }, [batchMappings]);

  const getBatchLabel = (slot: ActivitySlot): string => {
    const ids = (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds;
    return ids.map(id => batchNameMap.get(id) || `Batch ${id}`).join(', ');
  };

  // Compute time axis range: auto-expand beyond 07:00-21:00 if slots exist outside
  const { dayStartMinutes, dayEndMinutes, timeLabels } = useMemo(() => {
    let startMin = 7 * 60; // 07:00
    let endMin = 21 * 60;  // 21:00

    for (const s of slots) {
      if (s.startTime) {
        const sm = timeToMinutes(s.startTime);
        if (sm < startMin) startMin = Math.floor(sm / 60) * 60;
      }
      if (s.endTime) {
        const em = timeToMinutes(s.endTime);
        if (em > endMin) endMin = Math.ceil(em / 60) * 60;
      }
    }

    const labels: { minutes: number; label: string }[] = [];
    for (let m = startMin; m <= endMin; m += 30) {
      labels.push({ minutes: m, label: formatTimeDisplay(minutesToTime(m)) });
    }

    return { dayStartMinutes: startMin, dayEndMinutes: endMin, timeLabels: labels };
  }, [slots]);

  const totalHeight = ((dayEndMinutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN;

  // Position a slot on the continuous axis
  const getSlotStyle = useCallback((slot: ActivitySlot) => {
    const startMin = timeToMinutes(slot.startTime);
    const endMin = timeToMinutes(slot.endTime);
    const top = ((startMin - dayStartMinutes) / 30) * PIXELS_PER_30MIN;
    const height = Math.max(((endMin - startMin) / 30) * PIXELS_PER_30MIN, 24);
    return { top, height };
  }, [dayStartMinutes]);

  // Handle overlapping slots: assign column indices
  const slotColumns = useMemo(() => {
    const sorted = [...slots].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
    const columns: { slot: ActivitySlot; col: number; totalCols: number }[] = [];
    const active: { slot: ActivitySlot; col: number; endMin: number }[] = [];

    for (const slot of sorted) {
      const startMin = timeToMinutes(slot.startTime);
      const endMin = timeToMinutes(slot.endTime);

      // Remove expired slots from active set
      const stillActive = active.filter(a => a.endMin > startMin);
      const usedCols = new Set(stillActive.map(a => a.col));

      // Find first available column
      let col = 0;
      while (usedCols.has(col)) col++;

      stillActive.push({ slot, col, endMin });
      columns.push({ slot, col, totalCols: 0 }); // totalCols set below

      // Replace active with still active + current
      active.length = 0;
      active.push(...stillActive);
    }

    // Second pass: compute total columns for each overlap group
    for (const entry of columns) {
      const startMin = timeToMinutes(entry.slot.startTime);
      const endMin = timeToMinutes(entry.slot.endTime);
      let maxCol = entry.col;

      for (const other of columns) {
        const otherStart = timeToMinutes(other.slot.startTime);
        const otherEnd = timeToMinutes(other.slot.endTime);
        if (otherStart < endMin && otherEnd > startMin) {
          maxCol = Math.max(maxCol, other.col);
        }
      }
      entry.totalCols = maxCol + 1;
    }

    return columns;
  }, [slots]);

  const dateObj = parseLocalDate(date);
  const dateLabel = dateObj.toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const canGoPrev = date > templateStartDate;
  const canGoNext = date < templateEndDate;

  // Drag-and-drop handlers
  const handleDragStart = (e: React.DragEvent, slot: ActivitySlot) => {
    if (!isEditMode) return;
    setDraggedSlotId(slot.id);
    e.dataTransfer.setData('application/json', JSON.stringify({
      slotId: slot.id,
      startTime: slot.startTime,
      endTime: slot.endTime,
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isEditMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Show ghost position
    if (gridRef.current) {
      const rect = gridRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const rawMinutes = dayStartMinutes + (y / PIXELS_PER_30MIN) * 30;
      const snappedMinutes = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;
      const snappedTop = ((snappedMinutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN;

      // Get dragged slot duration
      try {
        const data = JSON.parse(e.dataTransfer.types.includes('application/json') ? '{}' : '{}');
        // We can't read dataTransfer during dragover (browser security), so use a default height
        setDragGhost({ top: snappedTop, height: PIXELS_PER_30MIN * 2 }); // 1 hour default
      } catch {
        setDragGhost({ top: snappedTop, height: PIXELS_PER_30MIN * 2 });
      }
    }
  };

  const handleDragLeave = () => {
    setDragGhost(null);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragGhost(null);
    setDraggedSlotId(null);

    if (!isEditMode || !gridRef.current || !templateId) return;

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (!data.slotId) return;

      const rect = gridRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const rawMinutes = dayStartMinutes + (y / PIXELS_PER_30MIN) * 30;
      const newStartMinutes = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;

      const oldDuration = timeToMinutes(data.endTime) - timeToMinutes(data.startTime);
      const newEndMinutes = newStartMinutes + oldDuration;

      const newStartTime = minutesToTime(newStartMinutes);
      const newEndTime = minutesToTime(newEndMinutes);

      // Don't update if same position
      if (newStartTime === data.startTime) return;

      // Call API to update (append :00 for seconds to match HH:mm:ss format)
      await ApiService.updateActivitySlotTemplate(templateId, data.slotId, {
        startTime: newStartTime + ':00',
        endTime: newEndTime + ':00',
      });

      onSlotMoved();
    } catch (err) {
      console.error('Failed to move slot:', err);
    }
  };

  const handleDragEnd = () => {
    setDraggedSlotId(null);
    setDragGhost(null);
  };

  // Click empty area to add slot at that time
  const handleGridClick = (e: React.MouseEvent) => {
    if (!isEditMode || !gridRef.current) return;
    // Don't trigger if clicking on a slot card
    if ((e.target as HTMLElement).closest('.day-slot-card')) return;

    const rect = gridRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const rawMinutes = dayStartMinutes + (y / PIXELS_PER_30MIN) * 30;
    const snappedMinutes = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;
    // We set the date, and the parent handles opening the modal
    // The startTime can be passed through the date param as a hint
    onAddSlot(date);
  };

  return (
    <div className="template-day-view">
      {/* Date navigation header */}
      <div className="day-view-header">
        <button
          className="day-nav-btn"
          onClick={() => onDateChange(addDays(date, -1))}
          disabled={!canGoPrev}
        >
          &lsaquo; Prev
        </button>
        <span className="day-view-title">{dateLabel}</span>
        <button
          className="day-nav-btn"
          onClick={() => onDateChange(addDays(date, 1))}
          disabled={!canGoNext}
        >
          Next &rsaquo;
        </button>
      </div>

      <div className="day-view-summary">
        {slots.length} slot{slots.length !== 1 ? 's' : ''} on this day
      </div>

      {/* Time grid */}
      <div className="day-view-grid-wrapper">
        <div className="day-time-labels">
          {timeLabels.map(({ minutes, label }) => (
            <div
              key={minutes}
              className="time-label"
              style={{ top: ((minutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN }}
            >
              {label}
            </div>
          ))}
        </div>

        <div
          className="day-time-grid"
          ref={gridRef}
          style={{ height: totalHeight }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleGridClick}
        >
          {/* Grid lines */}
          {timeLabels.map(({ minutes }) => (
            <div
              key={minutes}
              className={`grid-line ${minutes % 60 === 0 ? 'hour-line' : 'half-hour-line'}`}
              style={{ top: ((minutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN }}
            />
          ))}

          {/* Current time indicator */}
          {(() => {
            const now = new Date();
            const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            if (todayKey !== date) return null;
            const nowMinutes = now.getHours() * 60 + now.getMinutes();
            if (nowMinutes < dayStartMinutes || nowMinutes > dayEndMinutes) return null;
            return (
              <div
                className="current-time-line"
                style={{ top: ((nowMinutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN }}
              />
            );
          })()}

          {/* Slot cards */}
          {slotColumns.map(({ slot, col, totalCols }) => {
            const { top, height } = getSlotStyle(slot);
            const isDragging = draggedSlotId === slot.id;
            const widthPct = 100 / totalCols;
            const leftPct = col * widthPct;

            // Color based on instructor
            const hue = slot.instructorId ? (slot.instructorId * 137) % 360 : 210;

            return (
              <div
                key={slot.id}
                className={`day-slot-card ${isDragging ? 'dragging' : ''} ${slot.attendanceTaken ? 'attendance-done' : ''}`}
                style={{
                  top,
                  height,
                  left: `${leftPct}%`,
                  width: `${widthPct - 1}%`,
                  backgroundColor: `hsl(${hue}, 55%, 90%)`,
                  borderLeftColor: `hsl(${hue}, 65%, 55%)`,
                }}
                draggable={isEditMode && !slot.attendanceTaken}
                onDragStart={(e) => handleDragStart(e, slot)}
                onDragEnd={handleDragEnd}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isEditMode && !slot.attendanceTaken) {
                    onEditSlot(slot);
                  } else if (onAttendance && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0)) {
                    onAttendance(
                      (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                      slot.id,
                      slot.instructorId,
                      slot.instructorName
                    );
                  }
                }}
                title={`${formatTimeDisplay(slot.startTime)} - ${formatTimeDisplay(slot.endTime)}${slot.instructorName ? `\n${slot.instructorName}` : ''}${slot.topicName ? `\n${slot.topicName}` : ''}${slot.roomName ? `\nRoom: ${slot.roomName}${slot.centreName ? ` (${slot.centreName})` : ''}` : ''}${!isEditMode ? '\nClick to take attendance' : ''}`}
              >
                <div className="slot-card-time">
                  {formatTimeDisplay(slot.startTime)} - {formatTimeDisplay(slot.endTime)}
                </div>
                {height > 40 && slot.instructorName && (
                  <div className="slot-card-instructor">{slot.instructorName}</div>
                )}
                {height > 60 && slot.topicName && (
                  <div className="slot-card-topic">{slot.topicName}</div>
                )}
                {height > 70 && (slot.roomName || slot.centreName) && (
                  <div className="slot-card-room">
                    {slot.roomName && <span>🏫 {slot.roomName}</span>}
                    {slot.centreName && <span style={{marginLeft: 6}}>📍 {slot.centreName}</span>}
                  </div>
                )}
                {height > 90 && batchMappings.length > 0 && (
                  <div className="slot-card-batch">{getBatchLabel(slot)}</div>
                )}
                {height > 110 && slot.agenda && (
                  <div className="slot-card-agenda">{slot.agenda}</div>
                )}
                <div className="slot-card-actions">
                  {onAttendance && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0) && (
                    <button
                      className="slot-card-attendance"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAttendance(
                          (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                          slot.id,
                          slot.instructorId,
                          slot.instructorName
                        );
                      }}
                      title="Take Attendance (Java)"
                    >
                      A
                    </button>
                  )}
                  {onAttendanceNode && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0) && (
                    <button
                      className="slot-card-attendance"
                      style={{ background: 'var(--brand-primary)', color: 'var(--text-on-brand)' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAttendanceNode(
                          (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                          slot.id,
                          slot.instructorId,
                          slot.instructorName
                        );
                      }}
                      title="Take Attendance (Node — beta)"
                    >
                      A2
                    </button>
                  )}
                  {isEditMode && !slot.attendanceTaken && (
                    <button
                      className="slot-card-delete"
                      onClick={(e) => { e.stopPropagation(); onDeleteSlot(slot.id); }}
                      title="Delete"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Drag ghost */}
          {dragGhost && (
            <div
              className="drag-ghost"
              style={{ top: dragGhost.top, height: dragGhost.height }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateDayView;
